import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, map, Subscription, combineLatest, startWith, from, switchMap } from 'rxjs';
import { EditComponent } from '@workspaces/utils/edit-component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/state/app.state';
import { PersonaWithMetadata, WorkspacePersona } from '@workspaces/models/personas';
import { personaFeature, PersonaFetch } from '@workspaces/store/persona.feature';
import { workspacePersonaFeature, WorkspacePersonaFetch } from '@workspaces/store/workspace-persona.feature';
import { Persona } from 'src/app/models/personas.interface';
import { MatDialog } from '@angular/material/dialog';
import { PersonaSetComponent } from '../persona-set/persona-set.component';
import { CommonErrorLoadingComponent } from '../common-error-loading/common-error-loading.component';
import { RequiredRoleDirective } from '@workspaces/utils/workspace-guard.directive';
import { MarkdownModule, MarkdownService, ParseOptions } from 'ngx-markdown';
import { Renderer } from 'marked';
import { WorkspaceFetch, workspaceListFeature } from '@workspaces/store/workspace-list.feature';

@Component({
  selector: 'app-persona-management',
  templateUrl: './persona-management.component.html',
  styleUrls: ['./persona-management.component.scss', '../../global-workspace-styles.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatInputModule,
    MatDividerModule,
    MatTooltipModule,
    ReactiveFormsModule,
    CommonErrorLoadingComponent,
    RequiredRoleDirective,
    MarkdownModule
  ]
})
export class PersonaManagementComponent extends EditComponent implements OnInit, OnDestroy {
  private readonly sub = new Subscription();

  public personasWithMetadata$!: Observable<PersonaWithMetadata[]>;
  public personasWithMetadataIsError$!: Observable<boolean>;
  public personasWithMetadataIsLoading$!: Observable<boolean>;

  public saveInProgress: Record<string, boolean> = {};
  public isDisabledEdit$!: Observable<boolean>;
  constructor(
    private readonly store: Store<AppState>,
    protected override readonly route: ActivatedRoute,
    protected override readonly snackbar: MatSnackBar,
    protected override readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly markdownService: MarkdownService
  ) {
    super(route, snackbar, router);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.parseOptions = this.setParseOptions();

    this.initAccessControl();
    this.initDataStreams();

    this.store.dispatch(PersonaFetch({ includeHidden: true }));
    this.store.dispatch(WorkspacePersonaFetch({ workspaceId: this.workspaceId }));
    this.store.dispatch(WorkspaceFetch({ workspaceId: this.workspaceId }));
  }

  private initAccessControl(): void {
    this.isDisabledEdit$ = this.store.select(workspaceListFeature.workspaceById(this.workspaceId)).pipe(
      map(workspace => !!workspace?.is_archived || !!workspace?.is_deleted || workspace?.state === 'FINISHED'),
      startWith(false)
    );
  }

  private initDataStreams(): void {
    const persona$ = this.store.select(personaFeature.selectData);
    const workspacePersona$ = this.store.select(workspacePersonaFeature.workspacePersonaData(this.workspaceId));
    this.personasWithMetadata$ = combineLatest([persona$, workspacePersona$]).pipe(
      switchMap(
        ([persona, workspacePersona]) => from(this.combinePersonas(workspacePersona, persona)) // converts Promise → Observable
      )
    );
    this.personasWithMetadataIsError$ = combineLatest([
      this.personasWithMetadata$,
      this.store.select(workspacePersonaFeature.workspacePersonaIsError(this.workspaceId))
    ]).pipe(
      map(([hasData, isError]) => {
        if (!hasData?.length && isError) {
          return true;
        } else return false;
      })
    );

    const personaIsLoading$ = this.store.select(personaFeature.personaIsLoading);
    const workspacePersonaIsLoading$ = this.store.select(
      workspacePersonaFeature.workspacePersonaIsLoading(this.workspaceId)
    );
    this.personasWithMetadataIsLoading$ = combineLatest([
      personaIsLoading$.pipe(startWith(true)),
      workspacePersonaIsLoading$.pipe(startWith(true))
    ]).pipe(map(([personaIsLoading, workspacePersonaIsLoading]) => personaIsLoading && workspacePersonaIsLoading));
  }

  combinePersonas(workspacePersonas: WorkspacePersona[], personas: Persona[]) {
    if (!workspacePersonas || !personas) {
      return [];
    }

    const personaPromises = workspacePersonas.map(async workspacePersona => {
      const selectedPersonaMatch: Persona = personas.find(persona => persona.id === workspacePersona.persona_id) ?? {
        name: 'Unknown name',
        exampleDocuments: [],
        description: '',
        summary: ''
      };
      const parsedMd = await this.customMdParser(
        selectedPersonaMatch.description ?? selectedPersonaMatch.summary ?? workspacePersona.current_prompt
      );

      const personaWithMeta: PersonaWithMetadata = {
        ...workspacePersona,
        ...selectedPersonaMatch,
        description: parsedMd
      };
      return personaWithMeta;
    });
    return Promise.all(personaPromises);
  }

  parseOptions: ParseOptions = {};

  private setParseOptions(): ParseOptions {
    const renderer = new Renderer();

    renderer.list.bind(renderer);
    renderer.heading.bind(renderer);
    renderer.list = (body: string) => {
      return `<ul>${body}</ul>`;
    };
    renderer.heading = (text: string, level: number) => {
      return `<h${level} class="custom-md-h${level}">${text}</h${level}>`;
    };
    return {
      markedOptions: {
        renderer,
        hooks: {
          preprocess(markdown) {
            let updateMD = markdown
              .replace(/\\n/g, '\n')
              .replace(/\u2022/g, ' - ')
              .replace(/""/g, '___');

            if (updateMD.startsWith('"')) {
              updateMD = updateMD.slice(1);
            }
            return updateMD;
          },
          postprocess(html) {
            return html;
          },
          processAllTokens(tokens) {
            return tokens;
          },
          options: {}
        }
      }
    };
  }

  private customMdParser(md: string) {
    const result = this.markdownService.parse(md, this.parseOptions);
    return Promise.resolve(result).then(function (value) {
      return value;
    });
  }

  updateUsedPersonas() {
    const dialogRef = this.dialog.open(PersonaSetComponent, {
      maxWidth: '800px',
      data: this.workspaceId
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.store.dispatch(WorkspacePersonaFetch({ workspaceId: this.workspaceId }));
      }
    });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
